/**
 * Keeps the WSB appointment allowance current for the whole session.
 *
 * The shell never sees an appointment being created — creates go straight to
 * Core, and a booking made in the WSB widget happens on a public page with no
 * shell running at all. So it binds the `appointments.v2.created` Core pushes to
 * browsers, the same event calendar-app binds.
 *
 * The event carries an appointment id and its merchant, no counts, so it is a
 * nudge to re-read `/usage` rather than a source of figures.
 *
 * Core leaves two cases out of that push: appointment groups (course enrolments,
 * which count toward the cap) and staff who are neither admin nor owner, who
 * only receive appointments they are involved in. Those, the monthly reset, and
 * anything published while the socket was down fall to the backstops below.
 */
import type { QueryClient } from '@tanstack/react-query';

import { appointmentUsageFetched } from '../actions';
import { document } from '../env/browser';
import {
  APPOINTMENT_CREATED_EVENT,
  CALENDAR_CHANNEL,
} from '../features/bookings-upgrade/constants';
import type { AppShellApiClient } from '../shared/types';
import type { AppDispatch } from '../store';
import createDebug from '../utils/debug';
import { fetchAppointmentUsage } from '../utils/fetch-appointment-usage';

const debug = createDebug('appointment-created-push');

/**
 * The merchant an appointment event belongs to, or `null` if the payload is not
 * the shape Core sends. Asserts only the field it reads, so an unrelated
 * addition to the payload cannot start dropping events.
 */
function eventMerchantId(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null;

  const data = (payload as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;

  const merchant = (
    data as { relationships?: { merchant?: { data?: unknown } } }
  ).relationships?.merchant?.data;
  if (typeof merchant !== 'object' || merchant === null) return null;

  const id = (merchant as { id?: unknown }).id;
  return typeof id === 'string' ? id : null;
}

/**
 * Binds the appointment-created event and the two refetch backstops.
 *
 * Call for any capped merchant — the set `/usage` serves — not only one whose
 * bootstrap read succeeded. Registering after a failed read is what lets the
 * backstops pick the allowance up later in the session.
 *
 * `accountId` is the logged-in merchant account, not the merchant — the channel
 * is account-scoped and communication-service authorises no other.
 *
 * Never rejects: realtime being unavailable leaves the merchant on the
 * backstops rather than failing bootstrap.
 */
export function observeAppointmentCreatedPush(
  store: { dispatch: AppDispatch },
  {
    apiClient,
    queryClient,
  }: { apiClient: AppShellApiClient; queryClient: QueryClient },
  merchantId: string,
  accountId: string
): void {
  const refetch = () => {
    // `force` because every trigger here means the count has moved since any
    // request already open — see the fetcher. It never rejects: a failed re-read
    // leaves the previous numbers in place rather than clearing them, so a
    // network error cannot make a "limit reached" warning disappear.
    void fetchAppointmentUsage(merchantId, apiClient, queryClient, {
      force: true,
    }).then((usage) => {
      if (usage) store.dispatch(appointmentUsageFetched(usage));
    });
  };

  const onAppointmentCreated = (payload: unknown) => {
    const eventMerchant = eventMerchantId(payload);

    // A payload we cannot read means Core's shape moved. Re-read anyway rather
    // than going quiet, which would leave the counter frozen with nothing to say
    // so.
    if (eventMerchant === null) {
      debug('unreadable appointments.v2.created payload');
      refetch();
      return;
    }

    // An account can hold permissions on several merchants, so the channel
    // carries appointments for all of them.
    if (eventMerchant !== merchantId) return;

    refetch();
  };

  // Covers a backgrounded tab, the monthly reset, and the two cases Core's push
  // leaves out.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refetch();
  });

  void apiClient.realtime
    .connect()
    .then((state) => {
      if (state !== 'connected') return;

      apiClient.realtime
        .subscribe(`${CALENDAR_CHANNEL}=${accountId}`)
        .bind(APPOINTMENT_CREATED_EVENT, onAppointmentCreated);

      // Bound here rather than alongside the visibility listener: the client
      // refuses to bind connection state before a connection exists.
      apiClient.realtime.bind({ onConnected: refetch });
    })
    .catch(() => {
      // `connect` rejects on a failed handshake. The backstops still run.
    });
}
